import { getCurrentInstance, onBeforeUnmount, ref, watch } from 'vue';

import { useCapacityCalendarStore } from '@/store/capacityCalendarStore';

// Mirrors useRecentAssignmentHighlight's timer pattern, generalized to the store's
// recentCells (arbitrary `${rowId}:${weekKey}` cell keys) — flashes the cells a just-approved
// import wrote, rather than a just-assigned PO. Kept as a separate composable rather than
// widening useRecentAssignmentHighlight's contract, since the two flash sources (PO
// assignment vs. import) are keyed and triggered independently.
export function useRecentCellHighlight() {
  const store = useCapacityCalendarStore();
  const now = ref(Date.now());
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  watch(
    () => store.recentCells,
    (recent) => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      if (!recent) {
        return;
      }
      now.value = Date.now();
      const delay = Math.max(recent.until - Date.now(), 0);
      timeoutId = setTimeout(() => {
        now.value = Date.now();
      }, delay);
    },
    { immediate: true },
  );

  // Lifecycle hook only binds when called inside a component's setup();
  // the unit tests can call the composable bare.
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    });
  }

  function isRecentCell(rowId: string | undefined | null, weekKey: string | undefined | null): boolean {
    const recent = store.recentCells;
    if (!recent || !rowId || !weekKey) {
      return false;
    }
    if (now.value >= recent.until) {
      return false;
    }
    return recent.keys.has(`${rowId}:${weekKey}`);
  }

  return { isRecentCell };
}
